import * as Either from "features/common/either";
import { fetchJson, GenericError, HttpError } from "features/common/fetchJson";
import type * as AuthLoginResponse from "@silversea-ssc/dotcom-api-types/AuthLogin";
import type * as AuthLogoutResponse from "@silversea-ssc/dotcom-api-types/AuthLogout";
import type * as AuthStatusResponse from "@silversea-ssc/dotcom-api-types/AuthStatus";

const silentRedirectPath = "/auth/silent-login.html";

const getServerUrl = () => process.env.GATSBY_DIGITAL_API_BASE_URL + "/";

export const useAuthApi = () => {
  const loginUrl = async (
    redirectPath: string
  ): Promise<Either.Either<string, HttpError | GenericError>> => {
    const url = new URL("auth/login", getServerUrl());
    url.searchParams.set("redirectPath", redirectPath);

    const result = await fetchJson<AuthLoginResponse.AuthLoginResponseEncoded>(
      url.toString(),
      "Failed to get login URL"
    );
    return Either.map(result, (json) => json.authUrl);
  };

  const silentLoginUrl = async (): Promise<
    Either.Either<string, HttpError | GenericError>
  > => {
    const url = new URL("auth/login", getServerUrl());
    url.searchParams.set("redirectPath", silentRedirectPath);
    url.searchParams.set("silent", "true");

    const result = await fetchJson<AuthLoginResponse.AuthLoginResponseEncoded>(
      url.toString(),
      "Failed to get silent login URL"
    );
    return Either.map(result, (json) => json.authUrl);
  };

  const logoutUrl = async (
    redirectPath: string
  ): Promise<Either.Either<string | null, HttpError | GenericError>> => {
    const url = new URL("auth/logout", getServerUrl());
    url.searchParams.set("redirectPath", redirectPath);

    const result =
      await fetchJson<AuthLogoutResponse.AuthLogoutResponseEncoded>(
        url.toString(),
        "Failed to get logout URL",
        {
          method: "POST",
          credentials: "include"
        }
      );
    return Either.map(result, (json) => json.logoutUrl);
  };

  const status = (): Promise<
    Either.Either<
      AuthStatusResponse.AuthStatusResponseEncoded,
      HttpError | GenericError
    >
  > => {
    const url = new URL("auth/status", getServerUrl());

    return fetchJson<AuthStatusResponse.AuthStatusResponseEncoded>(
      url.toString(),
      "Failed to get auth status",
      {
        credentials: "include"
      }
    );
  };

  return {
    loginUrl,
    logoutUrl,
    silentLoginUrl,
    status
  };
};
